import './index.scss'

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'

import ErrorView from '@components/ErrorView'
import LoadingIndicator from '@components/LoadingIndicator'
import TrainingModuleCard from '@components/TrainingModuleCard'
import { useChurchDataProvider } from '@data/ChurchDataProvider'
import useTrainingModules from '@hooks/useTrainingModules'
import { TrainingModule } from '@interfaces/training.interface'
import ModuleEditorModal from '@pages/v2/Volunteering/ModuleEditorModal'

import bookIcon from 'url:@assets/icons/book.svg';
import plusIcon from 'url:@assets/icons/plusNoCircle.svg';

export default function VolunteeringTrainingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('training');
  const churchData = useChurchDataProvider();
  const { modules, loading, hasLoaded, error, refetch } = useTrainingModules();
  const campusGroups = groupModules(modules, churchData.campuses, churchData.categories);

  const [isModuleEditorOpen, setIsModuleEditorOpen] = useState(false);
  const [newModuleKey, setNewModuleKey] = useState(0);

  const openNewModuleEditor = () => {
    setNewModuleKey((currentKey) => currentKey + 1);
    setIsModuleEditorOpen(true);
  };

  const moduleEditor = <ModuleEditorModal
    key={`new-module-${newModuleKey}`}
    isOpen={isModuleEditorOpen}
    module={null}
    close={() => setIsModuleEditorOpen(false)}
    onSaved={() => {
      setIsModuleEditorOpen(false);
      void refetch();
    }}
  />;

  if (loading && !hasLoaded) {
    return <div className='training-page training-state'>
      <LoadingIndicator />
    </div>
  }

  if (error) {
    return <ErrorView
      error={error}
      buttonText={t('retry')}
      buttonAction={() => refetch()}
      fullHeight />
  }

  if (modules.length === 0) {
    return <>
      {moduleEditor}

      <ErrorView
        icon={bookIcon}
        title={t('emptyTitle')}
        error={t('emptyMessage')}
        buttonText={t('newModule')}
        buttonAction={openNewModuleEditor}
        fullHeight />
    </>
  }

  return <div className='training-page'>
    {moduleEditor}

    <div className='training-page-header'>
      <h2>{t('heading')}</h2>

      <button type='button' className='new-module-button' onClick={openNewModuleEditor}>
        <img src={plusIcon} alt='' aria-hidden='true' />
        {t('newModule')}
      </button>
    </div>

    {campusGroups.map((campusGroup) => (
      <section key={campusGroup.id} className='training-campus-group'>
        <div className='training-group-heading'>
          <h2>{campusGroup.title}</h2>
        </div>

        {campusGroup.categories.map((categoryGroup) => (
          <div key={categoryGroup.id} className='training-category-group'>
            <div className='training-group-subheading'>
              <h3>{categoryGroup.title}</h3>
            </div>

            <div className='training-module-grid'>
              {categoryGroup.modules.map((module) => {
                return <TrainingModuleCard
                  key={module.id}
                  module={module}
                  onClick={() => {
                    navigate(`/volunteering/training/${module.id}`, {
                      state: {
                        module
                      }
                    });
                  }}
                />
              })}
            </div>
          </div>
        ))}
      </section>
    ))}
  </div>
}

type CampusOption = {
  id: string;
  attributes: {
    name: string;
  };
};

type CategoryOption = {
  id: string;
  name: string;
};

function groupModules(
  modules: TrainingModule[],
  campuses: CampusOption[],
  categories: CategoryOption[]
) {
  const campusNameByID = new Map(campuses.map((campus) => [campus.id, campus.attributes.name]));
  const categoryNameByID = new Map(categories.map((category) => [category.id, category.name]));

  const sortedModules = [...modules].sort((one, two) => {
    const campusOne = getCampusLabel(one, campusNameByID);
    const campusTwo = getCampusLabel(two, campusNameByID);
    if (campusOne !== campusTwo) {
      return campusOne.localeCompare(campusTwo);
    }

    const categoryOne = getCategoryLabel(one, categoryNameByID);
    const categoryTwo = getCategoryLabel(two, categoryNameByID);
    if (categoryOne !== categoryTwo) {
      return categoryOne.localeCompare(categoryTwo);
    }

    return one.attributes.name.localeCompare(two.attributes.name);
  });

  const campusGroups = new Map<string, {
    id: string;
    title: string;
    categories: Map<string, {
      id: string;
      title: string;
      modules: TrainingModule[];
    }>;
  }>();

  sortedModules.forEach((module) => {
    const campusID = module.attributes.campusID;
    const categoryID = module.attributes.categoryID;

    if (!campusGroups.has(campusID)) {
      campusGroups.set(campusID, {
        id: campusID,
        title: getCampusLabel(module, campusNameByID),
        categories: new Map()
      });
    }

    const campusGroup = campusGroups.get(campusID)!;
    if (!campusGroup.categories.has(categoryID)) {
      campusGroup.categories.set(categoryID, {
        id: categoryID,
        title: getCategoryLabel(module, categoryNameByID),
        modules: []
      });
    }

    campusGroup.categories.get(categoryID)!.modules.push(module);
  });

  return Array.from(campusGroups.values()).map((campusGroup) => ({
    id: campusGroup.id,
    title: campusGroup.title,
    categories: Array.from(campusGroup.categories.values())
  }));
}

function getCampusLabel(module: TrainingModule, campusNameByID: Map<string, string>) {
  return campusNameByID.get(module.attributes.campusID) || i18next.t('training:unknownCampus');
}

function getCategoryLabel(module: TrainingModule, categoryNameByID: Map<string, string>) {
  return categoryNameByID.get(module.attributes.categoryID) || i18next.t('training:unknownCategory');
}
